import { Box } from '@mui/material';
import { ElementType, ReactNode } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import type { StatusColor } from '../../../../themes/v2/theme';
import Chip, { ChipProps } from '../Chip/Chip';
import Tooltip from '../Tooltip/Tooltip';
import StatusIcon from './components/StatusIcon';

export interface StatusChipProps {
  label: LangEnum;
  text?: string;
  icon: ElementType;
  color: StatusColor;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'plain';
  tooltip?: ReactNode;
  iconOnly?: boolean;
  component?: ChipProps['component'];
  clickable?: boolean;
  onClick?: ChipProps['onClick'];
}

const PLAIN_CHIP_SX = {
  backgroundColor: 'transparent',
  paddingInline: 0,
  '&:hover': { backgroundColor: 'transparent' },
} as const;

const StatusChip = ({
  label,
  text,
  icon,
  color,
  size = 'small',
  variant = 'filled',
  tooltip,
  iconOnly = false,
  component,
  clickable,
  onClick,
}: StatusChipProps) => {
  const { translate } = useTranslation();
  const translatedLabel = text ?? translate(label);
  const tooltipContent = tooltip ?? (iconOnly ? translatedLabel : undefined);
  const statusIcon = <StatusIcon icon={icon} color={color} size={size} />;

  return (
    <Tooltip TooltipContent={tooltipContent}>
      <Chip
        size={size}
        component={component}
        clickable={clickable}
        onClick={onClick}
        sx={variant === 'plain' ? PLAIN_CHIP_SX : undefined}
        label={
          iconOnly ? (
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', lineHeight: 0 }}>
              {statusIcon}
            </Box>
          ) : (
            translatedLabel
          )
        }
        icon={iconOnly ? undefined : statusIcon}
      />
    </Tooltip>
  );
};

export default StatusChip;
